import { React, useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import arrowDown from "../../images/arrow-down.svg";
import NoIndex from "../NoIndex";
import MyContext from "../../MyContext";
import Placeholder from "../../images/Place Holder 3 1.webp";
import Placeholder1 from "../../images/Place Holder.webp";
import Placeholder2 from "../../images/Place Holder_1.webp";

const CampaignView = ({ userID }) => {
  const { baseUrl, config } = useContext(MyContext);

  // Router
  const navigate = useNavigate();
  const location = useLocation();

  // Query params
  const [campaignId, setCampaignId] = useState("");
  const [campaignPhoneId, setCampaignPhoneId] = useState("");

  // Data
  const [campaigns, setCampaigns] = useState([]);
  const [camp, setCamp] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);

  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");

  // ----- Helpers / Styles -----
  const toggleDropdown = () => setIsOpen((s) => !s);

  const handleEditClick = () => {
    if (!campaignId) return;
    navigate(
      `/CampaignDetails?campaignId=${campaignId}&campaignName=${campaignPhoneId}`,
    );
  };

  const decodeBase64 = (text) => {
    if (!text) return "";
    try {
      return atob(text);
    } catch {
      return text;
    }
  };

  const getComponentText = (template, type) => {
    const comp = template?.components?.find((c) => c.type === type);
    return comp?.text ? decodeBase64(comp.text) : "";
  };

  const replaceVariablesBody = (text) => {
    const replacements = ["John Doe", "Messaging"]; // demo values; swap with real params if you have them
    return String(text || "").replace(
      /{{(\d+)}}/g,
      (_, index) => replacements[index - 1] || "",
    );
  };

  const pageWrap = {
    height: "100vh",
    background: "#f6f7fb",
    fontFamily: "Nunito, sans-serif",
    overflow: "hidden",
  };
  const headerBar = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #eef0f3",
    background: "#fff",
  };
  const btn = (bg, border, color = "#fff") => ({
    padding: "8px 12px",
    borderRadius: 8,
    border: `1px solid ${border}`,
    background: bg,
    color,
    cursor: "pointer",
  });
  const chip = (bg = "#eef7ff", color = "#0d6efd") => ({
    display: "inline-block",
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: bg,
    color,
    border: `1px solid ${color}1a`,
  });

  // ----- Read query params -----
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("campaignId") || "";
    const name = params.get("campaignName") || ""; // used as phoneNumberId in your fetch
    setCampaignId(id);
    setCampaignPhoneId(name);
  }, [location.search]);

  // ----- Fetchers -----
  const fetchCampaigns = async () => {
    const body = JSON.stringify({
      userid: userID,
      campaignName: "string",
      phoneNumberId: campaignPhoneId,
    });

    const res = await fetch(`${baseUrl}/api/Campaign/v1.0/getCampaigns`, {
      method: "POST",
      headers: config.headers,
      body,
    });
    if (!res.ok) throw new Error("Failed to fetch campaigns");
    const data = await res.json();
    if (data?.responseCode === "00") {
      setCampaigns(Array.isArray(data?.data) ? data.data : []);
    } else {
      throw new Error(data?.responseMessage || "Error loading campaigns");
    }
  };

  const fetchTemplates = async () => {
    const body = JSON.stringify({ userid: userID });
    const res = await fetch(`${baseUrl}/api/Whatsapp/getTemplates`, {
      method: "POST",
      headers: config.headers,
      body,
    });
    if (!res.ok) throw new Error("Failed to fetch templates");
    const data = await res.json();
    if (data?.responseCode === "00") {
      setTemplates(
        Array.isArray(data?.response?.data) ? data.response.data : [],
      );
    } else {
      throw new Error(data?.responseMessage || "Error loading templates");
    }
  };

  const refreshAll = async () => {
    setPageLoading(true);
    setError("");
    try {
      await Promise.all([fetchCampaigns(), fetchTemplates()]);
    } catch (e) {
      setError(e?.message || "Something went wrong");
    } finally {
      setPageLoading(false);
    }
  };

  // Initial + when phoneId changes
  useEffect(() => {
    if (campaignPhoneId === undefined) return;
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignPhoneId]);

  // Pick selected campaign when list or id changes
  useEffect(() => {
    if (!campaignId) return;
    const found = campaigns.find((c) => String(c?.id) === String(campaignId));
    setCamp(found || null);
  }, [campaigns, campaignId]);

  // Pick template corresponding to selected campaign
  useEffect(() => {
    if (!camp?.template_id || templates.length === 0) {
      setTemplate(null);
      return;
    }
    const t = templates.find((temp) => temp?.name === camp.template_id);
    setTemplate(t || null);
  }, [camp, templates]);

  // Re-trigger little preview animation on template change
  useEffect(() => {
    setAnimate(false);
    const id = setTimeout(() => setAnimate(true), 10);
    return () => clearTimeout(id);
  }, [template]);

  return (
    <>
      <NoIndex />
      {/* small keyframes for spinner */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={pageWrap}>
        {/* Header */}
        <div style={headerBar}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 className="m-0">Campaign Details</h2>
            {camp?.channel && (
              <span style={chip("#fff7e6", "#fd7e14")}>{camp.channel}</span>
            )}
            {camp?.template_id && (
              <span style={chip("#effaf4", "#198754")}>{camp.template_id}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              style={btn("#2F2CD8", "#2F2CD8")}
              onClick={refreshAll}
              disabled={pageLoading}
              title="Refresh"
            >
              {pageLoading ? "Refreshing…" : "Refresh"}
            </button>
            <button
              style={btn("#fff", "#ced4da", "#212529")}
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div
          className="p-4"
          style={{ height: "calc(100vh - 58px)", overflow: "auto" }}
        >
          {/* Loader */}
          {pageLoading && (
            <div
              style={{
                display: "flex",
                gap: 16,
                alignItems: "center",
                justifyContent: "center",
                height: "40vh",
                color: "#6c757d",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  border: "3px solid #e9ecef",
                  borderTopColor: "#0d6efd",
                  animation: "spin .8s linear infinite",
                }}
              />
              <span>Loading campaign & template…</span>
            </div>
          )}

          {/* Error */}
          {!pageLoading && error && (
            <div
              className="alert alert-danger"
              role="alert"
              style={{ borderRadius: 10 }}
            >
              {error}
            </div>
          )}

          {/* Not found */}
          {!pageLoading && !error && !camp && (
            <div
              className="alert alert-warning"
              role="alert"
              style={{ borderRadius: 10 }}
            >
              No campaign found for ID <b>{campaignId || "—"}</b>.
            </div>
          )}

          {/* Content */}
          {!pageLoading && !error && camp && (
            <div className="px-2">
              <div
                className="p-4 row col-12"
                style={{
                  borderRadius: "10px",
                  border: "1px solid #ccc",
                  backgroundColor: "#fff",
                }}
              >
                {/* Campaign Info Dropdown */}
                <div className="campaign-detail-info w-100">
                  <div
                    className="campaign-detail-header d-flex align-items-center justify-content-between"
                    onClick={toggleDropdown}
                    style={{
                      cursor: "pointer",
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: "1px solid #eef0f3",
                      background: "#fafafa",
                      fontWeight: 700,
                    }}
                  >
                    <span>Campaign Info</span>
                    <span
                      className={`campaign-detail-arrow ${isOpen ? "campaign-detail-open" : ""}`}
                    >
                      <img src={arrowDown} alt="toggle" />
                    </span>
                  </div>

                  <div
                    className={`campaign-detail-dropdown-content ${isOpen ? "campaign-detail-open" : ""}`}
                    style={{ marginTop: 10 }}
                  >
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Campaign Name:</th>
                          <td scope="col">{camp?.campaign_name || "—"}</td>
                          <th scope="col">Template Name:</th>
                          <td scope="col">{camp?.template_id || "—"}</td>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th scope="row">Start Date:</th>
                          <td>{camp?.campaign_start || "—"}</td>
                          <th>End Date:</th>
                          <td>{camp?.campaign_end || "—"}</td>
                        </tr>
                        <tr>
                          <th scope="row">Audience excel:</th>
                          <td>
                            {camp?.excel_url ? (
                              <a
                                href={camp.excel_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {camp.excel_url}
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <th>Channel:</th>
                          <td>{camp?.channel || "—"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr className="text-3-2" />

                {/* KPI Tiles */}
                <div className="d-flex flex-wrap gap-2">
                  {[
                    {
                      label: "Total Read",
                      color: "#007bff",
                      bg: "rgba(0,123,255,.1)",
                      value: camp?.read ?? 0,
                    },
                    {
                      label: "Total Delivered",
                      color: "#28a745",
                      bg: "rgba(40,167,69,.1)",
                      value: camp?.delivered ?? 0,
                    },
                    {
                      label: "Accepted",
                      color: "#17a2b8",
                      bg: "rgba(23,162,184,.1)",
                      value: camp?.accepted ?? 0,
                    },
                    {
                      label: "Failed",
                      color: "#dc3545",
                      bg: "rgba(220,53,69,.1)",
                      value: camp?.failed ?? 0,
                    },
                    {
                      label: "Total Responded",
                      color: "#ffc107",
                      bg: "rgba(255,193,7,.1)",
                      value: camp?.responded ?? 0,
                    },
                    {
                      label: "Total Sent",
                      color: "#6f42c1",
                      bg: "rgba(111,66,193,.1)",
                      value: camp?.sent ?? 0,
                    },
                  ].map((kpi, i) => (
                    <div
                      key={i}
                      style={{
                        border: `1px solid ${kpi.color}`,
                        backgroundColor: kpi.bg,
                        color: kpi.color,
                        textAlign: "center",
                        minWidth: 160,
                        flex: "1 1 200px",
                        borderRadius: 8,
                        padding: 10,
                      }}
                    >
                      {kpi.label}
                      <p style={{ fontWeight: "bold", marginBottom: 0 }}>
                        {kpi.value}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Details + Preview */}
                <div
                  className="d-flex justify-content-between w-100"
                  style={{ gap: 16 }}
                >
                  {/* Template Details */}
                  <div className="border rounded w-50 mt-4 p-2">
                    <div className="campaignheading ms-2">Template Details</div>
                    {!template ? (
                      <div className="text-muted ms-2 mb-2">
                        Template not found.
                      </div>
                    ) : (
                      <>
                        <div className="ms-2">
                          <div className="col-md-6 mb-1">
                            <div className="p-1">
                              <h5 className="text-dark">Name</h5>
                              <p>{template?.name || "—"}</p>
                            </div>
                          </div>
                          <div className="col-md-6 mb-1">
                            <div className="p-1">
                              <h5 className="text-dark">Category</h5>
                              <p>{template?.category || "—"}</p>
                            </div>
                          </div>
                          <div className="col-md-6 mb-1">
                            <div className=" p-1">
                              <h5 className="text-dark">Status</h5>
                              <p>{template?.status || "—"}</p>
                            </div>
                          </div>
                          <div className="col-md-6 mb-1">
                            <div className="p-1">
                              <h5 className="text-dark">Language</h5>
                              <p>{template?.language || "—"}</p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-1 ms-2">
                          {Array.isArray(template?.components) &&
                            template.components.map((component, index) => (
                              <div key={index} className="col-md-6 mb-1">
                                <div className="p-1">
                                  <h5 className="text-dark">
                                    {component?.type}
                                  </h5>
                                  <p>
                                    {component?.text
                                      ? decodeBase64(component.text)
                                      : "—"}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Phone Preview */}
                  {template && (
                    <div className="col-12 col-md-3 mt-md-0">
                      <div
                        className="mobile-card mt-4"
                        style={{ position: "relative" }}
                      >
                        <div className="mobile-btn1"></div>
                        <div className="mobile-btn2"></div>
                        <div className="mobile-btn3"></div>
                        <div className="mobile-btn4"></div>
                        <div className="mobile-card-int">
                          <div className="bubble-container">
                            <div
                              className={`message-bubble ${animate ? "message-bubble-animate" : ""}`}
                            >
                              {/* MEDIA */}
                              <div className="d-flex justify-content-center">
                                {template?.components?.find(
                                  (c) => c.format === "IMAGE",
                                ) && (
                                  <img
                                    src={Placeholder}
                                    alt=""
                                    style={{ width: "14rem" }}
                                  />
                                )}
                                {template?.components?.find(
                                  (c) => c.format === "VIDEO",
                                ) && (
                                  <img
                                    src={Placeholder1}
                                    alt=""
                                    style={{ width: "14rem" }}
                                  />
                                )}
                                {template?.components?.find(
                                  (c) => c.format === "DOCUMENT",
                                ) && (
                                  <img
                                    src={Placeholder2}
                                    alt=""
                                    style={{ width: "14rem" }}
                                  />
                                )}
                              </div>

                              {/* HEADER */}
                              <div className="header-text">
                                {getComponentText(template, "HEADER")}
                              </div>

                              {/* BODY */}
                              <div className="body-text py-1">
                                <div>
                                  {replaceVariablesBody(
                                    getComponentText(template, "BODY"),
                                  )}
                                </div>
                              </div>

                              {/* FOOTER */}
                              <div className="footer-text">
                                {getComponentText(template, "FOOTER")}
                              </div>

                              <div className="footer-text text-end">
                                05:27 PM
                              </div>
                            </div>

                            {/* Buttons */}
                            {template?.components
                              ?.find((c) => c.type === "BUTTONS")
                              ?.buttons?.map((button, index) => (
                                <div
                                  key={index}
                                  className={`button-bubble ${animate ? "button-bubble-animate" : ""}`}
                                >
                                  <div className="button-text text-success text-center mb-1">
                                    {button.text}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                        <div className="mobile-top">
                          <div className="mobile-camera">
                            <div className="mobile-int"></div>
                          </div>
                          <div className="mobile-speaker"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* /Details + Preview */}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CampaignView;
